<script lang="ts">
  import type { Snippet } from 'svelte'
  import { cn } from '$lib/utils'

  let {
    class: className,
    scrollClass,
    thumbClass,
    thumbPosition = 'bottom',
    thumbSticky = false,
    scrollAlign = 'start',
    style,
    children,
  }: {
    class?: string
    scrollClass?: string
    thumbClass?: string
    /** Where the custom thumb sits relative to the scroll content. */
    thumbPosition?: 'top' | 'bottom'
    /**
     * Pin the thumb while scrolling. For `top`, overlaps the content start
     * (negative margin) so layout is not shifted downward.
     */
    thumbSticky?: boolean
    /** Initial horizontal alignment when content overflows (e.g. double-page). */
    scrollAlign?: 'start' | 'center'
    style?: string
    children: Snippet
  } = $props()

  let scrollEl: HTMLElement | undefined = $state()
  let thumbRatio = $state(1)
  let thumbOffset = $state(0)
  const showThumb = $derived(thumbRatio < 0.999)
  /** Avoid re-centering after the user scrolls; reset when content width changes. */
  let lastCenteredScrollWidth = 0

  function syncThumb() {
    const el = scrollEl
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    if (scrollWidth <= 0) return
    thumbRatio = Math.min(1, clientWidth / scrollWidth)
    const maxScroll = scrollWidth - clientWidth
    thumbOffset =
      maxScroll > 0 ? (scrollLeft / maxScroll) * (1 - thumbRatio) * 100 : 0
  }

  function maybeCenterScroll() {
    const el = scrollEl
    if (!el || scrollAlign !== 'center') return
    const { scrollWidth, clientWidth } = el
    if (scrollWidth <= clientWidth) {
      lastCenteredScrollWidth = 0
      return
    }
    if (scrollWidth === lastCenteredScrollWidth) return
    lastCenteredScrollWidth = scrollWidth
    el.scrollLeft = (scrollWidth - clientWidth) / 2
  }

  $effect(() => {
    const el = scrollEl
    if (!el) return
    maybeCenterScroll()
    syncThumb()
    const onSize = () => {
      maybeCenterScroll()
      syncThumb()
    }
    const ro = new ResizeObserver(onSize)
    ro.observe(el)
    for (const child of el.children) ro.observe(child)
    const mo = new MutationObserver(() => {
      for (const child of el.children) ro.observe(child)
      onSize()
    })
    mo.observe(el, { childList: true, subtree: true })
    return () => {
      ro.disconnect()
      mo.disconnect()
    }
  })
</script>

{#snippet thumbTrack()}
  {#if showThumb}
    <div
      class={cn(
        'relative mx-3 h-1.5 shrink-0',
        // Sticky top: sit on the content's top edge without pushing it down.
        thumbSticky && thumbPosition === 'top' && 'sticky top-0 z-10 -mb-1.5',
        !thumbSticky && thumbPosition === 'top' && 'mb-1.5',
        thumbPosition === 'bottom' && 'mb-2',
        thumbClass,
      )}
    >
      <div
        class="absolute top-0 h-full rounded-full bg-gray-600/70"
        style="width: {thumbRatio * 100}%; left: {thumbOffset}%"
      ></div>
    </div>
  {/if}
{/snippet}

<div class={cn('min-w-0', className)} {style}>
  {#if thumbPosition === 'top'}
    {@render thumbTrack()}
  {/if}
  <div
    bind:this={scrollEl}
    onscroll={syncThumb}
    class={cn('overflow-x-auto overscroll-x-contain scrollbar-none', scrollClass)}
  >
    {@render children()}
  </div>
  {#if thumbPosition === 'bottom'}
    {@render thumbTrack()}
  {/if}
</div>
